import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, Loader2, Building2, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const Brand = () => (
  <div className="mb-8 flex items-center justify-center gap-2">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
      <Calculator className="h-6 w-6" />
    </div>
    <span className="font-display text-2xl font-extrabold tracking-tight text-foreground">
      TilloCash
    </span>
  </div>
);

/**
 * Shared signup form. When `inviteToken` + `businessName` are provided, the form
 * is in "join a business" mode: no business-name field, shows "Creating Account For".
 */
export default function Signup({ inviteToken = null, businessName = null }) {
  const { user, signup } = useAuth();
  const [business, setBusiness] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState("en");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const joining = !!inviteToken;

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup({
        email,
        password,
        name,
        language,
        business_name: joining ? null : business,
        invite_token: inviteToken,
      });
      toast.success(joining ? `Joined ${businessName}` : "Business account created");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <Brand />
        <Card className="rounded-2xl border-border p-8">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-foreground">
            {joining ? "Join your team" : "Create your account"}
          </h1>

          {joining ? (
            <div
              data-testid="creating-account-for"
              className="mt-4 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4"
            >
              <Building2 className="h-6 w-6 shrink-0 text-primary" />
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary">
                  Creating Account For
                </p>
                <p className="font-display text-xl font-extrabold text-foreground" data-testid="join-business-name">
                  {businessName}
                </p>
              </div>
            </div>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              Start a new business and invite your team later.
            </p>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {!joining && (
              <div>
                <Label htmlFor="business" className="text-sm font-semibold">Business Name</Label>
                <Input
                  id="business"
                  value={business}
                  onChange={(e) => setBusiness(e.target.value)}
                  data-testid="signup-business-input"
                  placeholder="e.g. Downtown Cafe"
                  className="mt-1.5 h-12"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="name" className="text-sm font-semibold">Your Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="signup-name-input"
                placeholder="So your team knows who you are"
                className="mt-1.5 h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email-input"
                className="mt-1.5 h-12"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password-input"
                placeholder="At least 6 characters"
                className="mt-1.5 h-12"
                required
              />
            </div>
            {error && (
              <p data-testid="signup-error" className="text-sm font-medium text-rose-600">
                {error}
              </p>
            )}
            <div>
              <Label htmlFor="language" className="text-sm font-semibold">Choose Language</Label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="signup-language-select"
                className="mt-1.5 h-12 w-full rounded-md border border-input bg-background px-3 text-base"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-btn"
              className="h-12 w-full rounded-xl font-bold active:scale-[0.98]"
            >
              {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              {joining ? `Join ${businessName}` : "Create account"}
            </Button>
          </form>
        </Card>

        {joining ? (
          <button
            onClick={() => navigate("/login")}
            data-testid="cancel-join-btn"
            className="mt-5 flex w-full items-center justify-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Cancel — I don't want to join this business
          </button>
        ) : (
          <p className="mt-5 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="font-semibold text-primary hover:underline">
              Log in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
